import type {HierarchyServiceAccountListRecordFetcher} from "app/services/domain/service-accounts/account-governance-list.service";
import {ServiceAccountHierarchyNode} from "app/services/domain/service-accounts/service-account-hierarchy";
import {ServiceAccountService} from "app/services/domain/service-accounts/service-account.service";
import {ServiceAccountDetailsExtended} from "app/services/domain/service-accounts/service-accounts";
import {WidgetConfigurationExtended} from "app/services/domain/widget-data.service";
import * as Models from "app/services/proxy/model/models";

import {Lookup, UtilsService} from "framework/services/utils.service";
import {ControlOption} from "framework/ui/control-option";

export interface LazyAccountTypeAggregationOptions
{
    // Restrict the derived options to types that can own descendants (mirrors loadDimensionAccountTypes)
    parentCapableTypesOnly?: boolean;

    // Dropdown widgets fall back to the first derived type when the requested one is absent;
    // cost-summary keeps strict semantics (absent type -> no data).
    fallbackToFirstType?: boolean;

    // Lets the widget populate its dropdown from the same fetch that filtered the data.
    onTypesResolved?: (resolution: LazyAccountTypeResolution) => void;
}

export interface LazyAccountTypeResolution
{
    typeOptions: ControlOption<Models.ServiceAccountNativeDetailsBase>[];
    effectiveType: Models.ServiceAccountNativeDetailsBase | undefined; // reference-equal to a typeOptions[i].id
}

export interface LazyAccountTypeGrouping extends LazyAccountTypeResolution
{
    nodes: ServiceAccountHierarchyNode[]; // one per target ancestor; children = contributing response accounts
}

type NativeDetailsConstructor = new () => Models.ServiceAccountNativeDetailsBase;

export function accountTypeDescriptorName(nativeDetails: Models.ServiceAccountNativeDetailsBase): string
{
    const model = Models.ServiceAccountDetails.newInstance({native_details: nativeDetails});
    return ServiceAccountDetailsExtended.getDescriptorForModel(model)?.config?.name;
}

// Consolidates the account types available in a billing response and the per-account grouping target,
// using only the hierarchy fetcher's batch API (never the full-tree enumeration).
export async function resolveLazyAccountTypeGrouping(serviceAccountIds: string[],
                                                     requestedType: Models.ServiceAccountNativeDetailsBase | undefined,
                                                     fetcher: HierarchyServiceAccountListRecordFetcher,
                                                     options: LazyAccountTypeAggregationOptions): Promise<LazyAccountTypeGrouping>
{
    if (!fetcher?.includesParentInfo) throw new Error("resolveLazyAccountTypeGrouping requires a fetcher configured with parent_accounts_info");

    const lookup = serviceAccountIds.length ? await fetcher.getBatchAsLookup(UtilsService.dedupeElements(serviceAccountIds)) : {};

    const typeOptions   = deriveAccountTypeOptions(lookup, !!options.parentCapableTypesOnly);
    const effectiveType = resolveEffectiveType(typeOptions, requestedType, !!options.fallbackToFirstType);
    if (!effectiveType)
    {
        return {
            typeOptions,
            effectiveType,
            nodes: []
        };
    }

    // Fetch every type-matching ancestor: their depths disambiguate nested same-type ancestors, and
    // caching their details in the fetcher lets downstream identity resolution name the rolled-up rows.
    const matchingAncestorIds = new Set<string>();
    for (const details of Object.values(lookup))
    {
        for (const parent of matchingParents(details, effectiveType))
        {
            if (!lookup[parent.parent_id]) matchingAncestorIds.add(parent.parent_id);
        }
    }
    const ancestorLookup = matchingAncestorIds.size ? await fetcher.getBatchAsLookup([...matchingAncestorIds]) : {};

    return {
        typeOptions,
        effectiveType,
        nodes: buildAncestorGroupingNodes(lookup, effectiveType, ancestorLookup)
    };
}

// Unique account types among {response accounts ∪ their ancestors}, labelled like the eager tree walk
// (descriptor config name, deduped by descriptor name). Also seeds the descriptor map that the eager
// enumeration would have populated, since summary labels/icons read from it.
export function deriveAccountTypeOptions(lookup: Lookup<Models.ServiceAccountDetailsWithAdditionalInfo>,
                                         parentCapableTypesOnly: boolean): ControlOption<Models.ServiceAccountNativeDetailsBase>[]
{
    const byDescriptorName = new Map<string, Models.ServiceAccountNativeDetailsBase>();

    const consider = (nativeDetails: Models.ServiceAccountNativeDetailsBase) =>
    {
        if (!nativeDetails) return;
        if (parentCapableTypesOnly && !isParentCapableType(nativeDetails)) return;

        const descriptorName = accountTypeDescriptorName(nativeDetails);
        if (!descriptorName) return; // abstract/cluster types without a registered descriptor

        if (!byDescriptorName.has(descriptorName)) byDescriptorName.set(descriptorName, nativeDetails);

        const detailsConstructor = <typeof Models.ServiceAccountNativeDetailsBase>nativeDetails.constructor;
        if (!ServiceAccountService.accountTypeDescriptorMap.has(detailsConstructor))
        {
            ServiceAccountService.accountTypeDescriptorMap.set(detailsConstructor, descriptorName);
        }
    };

    for (const details of Object.values(lookup ?? {}))
    {
        consider(details?.native_details);
        for (const parent of details?.parent_accounts_info ?? [])
        {
            consider(parent?.native_details);
        }
    }

    return [...byDescriptorName.entries()]
        .sort(([a],
               [b]) => UtilsService.compareStrings(a, b, true))
        .map(([descriptorName, nativeDetails]) => new ControlOption<Models.ServiceAccountNativeDetailsBase>(nativeDetails, descriptorName));
}

export function resolveEffectiveType(typeOptions: ControlOption<Models.ServiceAccountNativeDetailsBase>[],
                                     requestedType: Models.ServiceAccountNativeDetailsBase | undefined,
                                     fallbackToFirstType: boolean): Models.ServiceAccountNativeDetailsBase | undefined
{
    const requestedConstructor = <NativeDetailsConstructor>requestedType?.constructor;
    const matched              = requestedConstructor && typeOptions.find(option => option.id instanceof requestedConstructor)?.id;
    return matched || (fallbackToFirstType ? typeOptions[0]?.id : undefined);
}

// For each response account, resolve the root-most self-or-ancestor of the selected type (eager
// collectSubtreesWithSelectedRootType stops at the outermost matching node and subsumes its subtree)
// and group the account under it. Accounts with no matching self-or-ancestor are excluded.
export function buildAncestorGroupingNodes(lookup: Lookup<Models.ServiceAccountDetailsWithAdditionalInfo>,
                                           effectiveType: Models.ServiceAccountNativeDetailsBase,
                                           ancestorLookup: Lookup<Models.ServiceAccountDetailsWithAdditionalInfo>): ServiceAccountHierarchyNode[]
{
    const effectiveConstructor = <NativeDetailsConstructor>effectiveType.constructor;
    const depthOf              = (id: string): number =>
    {
        // parent_accounts_info holds the full ancestor chain, so its size is the node's depth;
        // the array order itself is not guaranteed and must not be used for root-most resolution.
        const details = lookup[id] ?? ancestorLookup[id];
        return details ? details.parent_accounts_info?.length ?? 0 : Number.MAX_SAFE_INTEGER;
    };

    const contributorsByTarget = new Map<string, string[]>();
    for (const [id, details] of Object.entries(lookup ?? {}))
    {
        if (!details) continue;

        const candidateIds = matchingParents(details, effectiveType)
            .map(parent => parent.parent_id);
        if (details.native_details instanceof effectiveConstructor) candidateIds.push(id);
        if (!candidateIds.length) continue;

        let targetId = candidateIds[0];
        for (const candidateId of candidateIds)
        {
            if (depthOf(candidateId) < depthOf(targetId)) targetId = candidateId;
        }

        const contributors = contributorsByTarget.get(targetId) ?? [];
        contributorsByTarget.set(targetId, contributors);
        if (id !== targetId) contributors.push(id);
    }

    const nodes: ServiceAccountHierarchyNode[] = [];
    for (const [targetId, contributors] of contributorsByTarget)
    {
        const node = new ServiceAccountHierarchyNode(targetId, lookup[targetId] ?? ancestorLookup[targetId] ?? null);
        for (const contributorId of contributors)
        {
            node.addChild(new ServiceAccountHierarchyNode(contributorId, lookup[contributorId]));
        }
        nodes.push(node);
    }
    return nodes;
}

function matchingParents(details: Models.ServiceAccountDetailsWithAdditionalInfo,
                         effectiveType: Models.ServiceAccountNativeDetailsBase): Models.ServiceAccountParentInfo[]
{
    const effectiveConstructor = <NativeDetailsConstructor>effectiveType.constructor;
    return (details?.parent_accounts_info ?? []).filter(parent => parent?.parent_id && parent.native_details instanceof effectiveConstructor);
}

function isParentCapableType(nativeDetails: Models.ServiceAccountNativeDetailsBase): boolean
{
    return WidgetConfigurationExtended.s_accountTypeNativeDetailsEntries.some(([key]) => nativeDetails.constructor === key.constructor);
}
